import { FC, useState } from 'react';
import { Form, FormGroup, Input, Label, Modal, ModalHeader, ModalBody } from 'reactstrap';
import Select from 'react-select';
import { Button, Close, ReactSelectOverrides } from '@bphxd/ds-core-react';
import { Trash24 } from '@bphxd/ds-core-react/lib/icons';
import { optionsFromEnum, stringToOption } from '../../../lib/util/format';
import { CERTIFICATE_STATUS_ENUM, TEMPLATE_OPTIONS } from '../../../lib/constants/dbConstants';
import { TAspectsTableFilters } from '../AspectsTable/AspectsTable';
import classes from '../JobFiltersModal/JobFiltersModal.module.scss';
import clsx from 'clsx';
import { useSelectedAsset } from '../../../lib/util';
import { getCertificateAreaOptions } from '../../../lib/util/certificateOptions';
import { stepTitles } from '../../pages/CertificateMonitoring/CertificateMonitoring';

export type TCertificateFilters = TAspectsTableFilters<
  | 'isolationNumber'
  | 'mc1'
  | 'sh1'
  | 'maintenanceOrder'
  | 'permitNumber'
  | 'status'
  | 'system'
  | 'template'
  | 'area'
  | 'completedStep'
>;

const CertificateFiltersModal: FC<{
  defaultValues: TCertificateFilters;
  onSubmit: (filters: TCertificateFilters) => unknown;
  onReset: () => void;
  onClose: () => void;
  showMonitoringFilters?: boolean;
}> = ({ defaultValues, onSubmit, onReset, onClose, showMonitoringFilters = false }) => {
  const [filters, setFilters] = useState<TCertificateFilters>(defaultValues);
  const asset = useSelectedAsset();
  const assetId = asset?.assetId || -1;
  const setFilterByKey = (key: keyof TCertificateFilters, values: string[]) => {
    setFilters((filters) => ({ ...filters, [key]: values }));
  };
  const handleSubmit = () => {
    onSubmit(filters);
    onClose();
  };

  const CERTIFICATE_AREA_OPTIONS = getCertificateAreaOptions(assetId);

  return (
    <Modal isOpen toggle={onClose} className="modal-dialog-centered">
      <ModalHeader className="border-0 px-6" close={<Close onClick={onClose} />}>
        Filter Certificates
      </ModalHeader>
      <ModalBody className={clsx('p-6 pt-0', classes.root)}>
        <div className={classes.filters}>
          <Form className="row g-5">
            <div className="col-md-6">
              <FormGroup>
                <Label>Isolation Number</Label>
                <Input
                  type="text"
                  title="Use comma to separate multiple values like 'A123,B456'. Values need to be exact."
                  name="isolationNumber"
                  id="isolation-number"
                  defaultValue={filters.isolationNumber}
                  onChange={(e) => {
                    const isolationNumberArrary: string[] = e.target.value.split(',');
                    setFilterByKey('isolationNumber', isolationNumberArrary);
                  }}
                />
              </FormGroup>
            </div>
            <div className="col-md-6">
              <FormGroup>
                <Label>Permit Number</Label>
                <Input
                  type="text"
                  title="Use comma to separate multiple values like 'A123,B456'. Values need to be exact."
                  name="permitNumber"
                  id="permit-number"
                  defaultValue={filters.permitNumber}
                  onChange={(e) => {
                    const permitNumberArrary: string[] = e.target.value.split(',');
                    setFilterByKey('permitNumber', permitNumberArrary);
                  }}
                />
              </FormGroup>
            </div>
            <div className="col-md-6">
              <FormGroup>
                <Label>MC1</Label>
                <Input
                  type="text"
                  title="Use comma to separate multiple values like 'A123,B456'. Values need to be exact."
                  name="mc1Number"
                  id="mc1-number"
                  defaultValue={filters['mc1']}
                  onChange={(e) => {
                    const mc1NumberArrary: string[] = e.target.value.split(',');
                    setFilterByKey('mc1', mc1NumberArrary);
                  }}
                />
              </FormGroup>
            </div>
            <div className="col-md-6">
              <FormGroup>
                <Label>SH1</Label>
                <Input
                  type="text"
                  title="Use comma to separate multiple values like 'A123,B456'. Values need to be exact."
                  name="sh1Number"
                  id="sh1-number"
                  defaultValue={filters['sh1']}
                  onChange={(e) => {
                    const sh1NumberArrary: string[] = e.target.value.split(',');
                    setFilterByKey('sh1', sh1NumberArrary);
                  }}
                />
              </FormGroup>
            </div>
            <div className="col-md-6">
              <FormGroup>
                <Label>Area</Label>
                <Select
                  defaultValue={filters.area.map(stringToOption)}
                  isMulti
                  options={CERTIFICATE_AREA_OPTIONS}
                  hideSelectedOptions={false}
                  closeMenuOnSelect={false}
                  components={ReactSelectOverrides.default}
                  classNamePrefix="react-select"
                  onChange={(newSelection) => {
                    setFilterByKey(
                      'area',
                      newSelection.map((o) => o.value)
                    );
                  }}
                />
              </FormGroup>
            </div>
            {!showMonitoringFilters && (
              <div className="col-md-6">
                <FormGroup>
                  <Label>Certificate Status</Label>
                  <Select
                    defaultValue={filters.status.map(stringToOption)}
                    isMulti
                    options={optionsFromEnum(CERTIFICATE_STATUS_ENUM)}
                    hideSelectedOptions={false}
                    closeMenuOnSelect={false}
                    components={ReactSelectOverrides.default}
                    classNamePrefix="react-select"
                    onChange={(newSelection) => {
                      setFilterByKey(
                        'status',
                        newSelection.map((o) => o.value)
                      );
                    }}
                  />
                </FormGroup>
              </div>
            )}
            {!showMonitoringFilters && (
              <div className="col-md-6">
                <FormGroup>
                  <Label>Template</Label>
                  <Select
                    defaultValue={filters.template.map(stringToOption)}
                    isMulti
                    options={TEMPLATE_OPTIONS}
                    hideSelectedOptions={false}
                    closeMenuOnSelect={false}
                    components={ReactSelectOverrides.default}
                    classNamePrefix="react-select"
                    onChange={(newSelection) => {
                      setFilterByKey(
                        'template',
                        newSelection.map((o) => o.value)
                      );
                    }}
                  />
                </FormGroup>
              </div>
            )}
            {showMonitoringFilters && (
              <div className="col-md-6">
                <FormGroup>
                  <Label>System</Label>
                  <Input
                    type="text"
                    title="Use comma to separate multiple values like 'A123,B456'. Values need to be exact."
                    name="system"
                    id="system"
                    defaultValue={filters.system}
                    onChange={(e) => {
                      const systemArray: string[] = e.target.value.split(',');
                      setFilterByKey('system', systemArray);
                    }}
                  />
                </FormGroup>
              </div>
            )}

            {showMonitoringFilters && (
              <div className="col-md-6">
                <FormGroup>
                  <Label>Completed Step</Label>
                  <Select
                    placeholder="Choose completed step..."
                    defaultValue={filters.completedStep.map(stringToOption)}
                    isMulti={false}
                    isClearable
                    options={stepTitles.map(stringToOption)}
                    hideSelectedOptions={false}
                    closeMenuOnSelect={false}
                    components={ReactSelectOverrides.default}
                    classNamePrefix="react-select"
                    onChange={(newSelection) => {
                      setFilterByKey(
                        'completedStep',
                        newSelection?.value ? [newSelection.value] : []
                      );
                    }}
                  />
                </FormGroup>
              </div>
            )}

            <div className={clsx('col-md-12', classes.footer)}>
              <Button
                size="lg"
                className=""
                theme="darker"
                level="tertiary"
                onClick={onReset}
                Icon={Trash24}
              >
                Reset Filters
              </Button>
              <Button
                disabled={filters === defaultValues}
                size="md"
                className=""
                theme="darker"
                level="primary"
                onClick={handleSubmit}
              >
                Apply Filters
              </Button>
            </div>
          </Form>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default CertificateFiltersModal;
